"use client"

import type React from "react"
import { useState, useCallback, useMemo, useRef } from "react"
import { X, AlertTriangle } from "lucide-react"
import type { DynamicUser, DynamicSchema } from "../types/dynamic"
import { SchemaService } from "../services/schema"
import { updateDynamicUserByUniqueField } from "../services/api"
import { addToQueue } from "../services/queue"
import { updateDynamicUserInDB } from "../services/database"
import { useNetworkStatus } from "../hooks/useNetworkStatus"
import { sendToPrinter } from "../services/printer"
import { LabelPrinter } from "./LabelPrinter"
import type { LabelField, LabelFixedField } from "./LabelPrinter"
import {
  isValidHexColor,
  resolveBadgeHex,
  resolveBadgeLabel,
  getTextColorForBg,
} from "../utils/badgeColors"

interface DynamicPrintModalProps {
  user: DynamicUser
  schema: DynamicSchema
  onClose: () => void
  onPrintSuccess: (user: DynamicUser) => void
  showToast: (type: "success" | "error" | "info", message: string) => void
  cancelCurrentFetch: () => void
  printedBy?: "panel" | "kiosko"
}

const DynamicPrintModal: React.FC<DynamicPrintModalProps> = ({
  user,
  schema,
  onClose,
  onPrintSuccess,
  showToast,
  cancelCurrentFetch,
  printedBy = "panel",
}) => {
  const [localUser, setLocalUser] = useState<DynamicUser>(() => JSON.parse(JSON.stringify(user)))
  const [loading] = useState(false)
  const [fieldSaving, setFieldSaving] = useState<Record<string, boolean>>({})
  const [hasUnsyncedChanges, setHasUnsyncedChanges] = useState(false)
  const [showReprintConfirm, setShowReprintConfirm] = useState(false)
  // Resolver de la Promise que LabelPrinter está esperando en onBeforePrint
  const reprintResolverRef = useRef<((proceed: boolean) => void) | null>(null)
  const isOnline = useNetworkStatus()

  // Verificar si es etiqueta genérica (ID 0)
  const isGenericLabel = localUser.id === 0

  // Campos requeridos para impresión que están vacíos
  const missingPrintFields = isGenericLabel
    ? []
    : Object.entries(schema)
        .filter(([, f]) => f.print === true && f.required === true)
        .filter(([fieldName]) => {
          const val = SchemaService.getFieldValue(localUser, fieldName)
          return val === null || val === undefined || (typeof val === 'string' && val.trim() === '')
        })
        .map(([, f]) => f.label)

  // Estado para sistema PATCH - Solo para usuarios reales
  const [originalValues, setOriginalValues] = useState<Record<string, any>>(() => {
    if (isGenericLabel) return {}
    const original: Record<string, any> = {}
    Object.entries(schema).forEach(([fieldName, fieldConfig]) => {
      if (fieldConfig.print && fieldConfig.editable !== false) {
        original[fieldName] = SchemaService.getFieldValue(user, fieldName) ?? ''
      }
    })
    return original
  })

  // Convertir schema dinámico a campos de LabelPrinter
  const convertToLabelFields = useCallback((): LabelField[] => {
    return Object.entries(schema)
      .filter(([_, fieldSchema]) => fieldSchema.print === true)
      .filter(([_, fieldSchema]) => !fieldSchema.fixed) // Excluir campo fixed (va separado)
      .sort((a, b) => (a[1].position || 999) - (b[1].position || 999))
      .map(([fieldName, fieldSchema]) => ({
        name: fieldName,
        label: fieldSchema.label,
        value: SchemaService.getFieldValue(localUser, fieldName)?.toString() || '',
        type: fieldSchema.type === 'number' ? 'number' : 
              fieldSchema.type === 'enum' ? 'enum' : 'text',
        options: fieldSchema.options,
        required: fieldSchema.required,
        editable: isGenericLabel ? true : fieldSchema.editable !== false
      }))
  }, [localUser, schema, isGenericLabel])

  // Obtener campo fijo para QR
  const getFixedField = useCallback((): LabelFixedField | undefined => {
    const fixedFieldName = SchemaService.getFixedField(schema)
    
    if (isGenericLabel) {
      // Para etiquetas genéricas, siempre permitir QR editable
      if (fixedFieldName) {
        const fixedValue = SchemaService.getFieldValue(localUser, fixedFieldName)
        return {
          value: String(fixedValue || 'QR_GENERICO_001'),
          label: schema[fixedFieldName]?.label || 'Código QR',
          editable: true
        }
      } else {
        // Crear campo QR dinámico si no existe en el schema
        return {
          value: 'QR_GENERICO_001',
          label: 'Código QR',
          editable: true
        }
      }
    } else {
      // Para usuarios reales, usar el campo fijo del schema
      if (!fixedFieldName) return undefined
      
      const fixedValue = SchemaService.getFieldValue(localUser, fixedFieldName)
      if (!fixedValue) return undefined

      return {
        value: String(fixedValue),
        label: schema[fixedFieldName]?.label || 'Código QR',
        editable: false // No editable para usuarios reales
      }
    }
  }, [localUser, schema, isGenericLabel])

  // Obtener categoría para watermark
  const getCategoryValue = useCallback((): string => {
    return SchemaService.getFieldValue(localUser, 'categoria') ||
           SchemaService.getFieldValue(localUser, 'category') ||
           ''
  }, [localUser])

  // REQ-003: Barra de color del gafete (footer)
  // Orden de resolución:
  //   1. badge_color directo (hex o nombre de tipo, ej. "visitante", "#004d95")
  //   2. categoria (fallback) → buscar en BADGE_GROUPS
  const badgeColorFooter = useMemo(() => {
    const rawBadge = String(
      SchemaService.getFieldValue(localUser, "badge_color") || "",
    ).trim()
    const rawCategoria = String(
      SchemaService.getFieldValue(localUser, "categoria") ||
        SchemaService.getFieldValue(localUser, "category") ||
        "",
    ).trim()

    const resolvedHex =
      resolveBadgeHex(rawBadge) || resolveBadgeHex(rawCategoria)
    const isValid = !!resolvedHex && isValidHexColor(resolvedHex)

    // Mostrar nombre del color + categoría (ej. "AZUL · VISITANTE")
    const colorName = isValid ? resolveBadgeLabel(resolvedHex!) : null
    const categoryName = rawCategoria || rawBadge
    const displayText = !isValid
      ? "COLOR NO DEFINIDO"
      : [colorName, categoryName].filter(Boolean).join(" · ") ||
        resolvedHex!.toUpperCase()

    return (
      <div
        className={`h-12 rounded-xl flex items-center justify-center ${
          isValid ? "shadow-md" : "bg-gray-100 border border-dashed border-gray-300"
        }`}
        style={isValid ? { backgroundColor: resolvedHex! } : undefined}
        title={
          isValid
            ? `Color del gafete: ${displayText} (${resolvedHex!.toUpperCase()})`
            : "Color no definido"
        }
      >
        <span
          className="text-sm font-semibold tracking-wide uppercase"
          style={
            isValid ? { color: getTextColorForBg(resolvedHex!) } : { color: "#9ca3af" }
          }
        >
          {displayText}
        </span>
      </div>
    )
  }, [localUser])

  // Verificar si un campo ha cambiado
  const hasFieldChanged = (fieldName: string, currentValue: string): boolean => {
    if (isGenericLabel) return false
    const originalValue = originalValues[fieldName] ?? ''
    return String(currentValue) !== String(originalValue)
  }

  // Guardar cambio individual de campo
  const saveFieldChange = async (fieldName: string, newValue: string) => {
    if (isGenericLabel || !hasFieldChanged(fieldName, newValue)) return
    
    setFieldSaving(prev => ({ ...prev, [fieldName]: true }))
    
    try {
      const uniqueField = SchemaService.getUniqueField(schema)
      if (!uniqueField) {
        throw new Error("No se encontró campo único para la actualización")
      }
      
      const uniqueFieldValue = SchemaService.getFieldValue(user, uniqueField)
      if (!uniqueFieldValue) {
        throw new Error(`Valor del campo único ${uniqueField} no encontrado`)
      }

      // Crear payload PATCH
      const updateData: any = {}
      const mainFields = ['nombre', 'apellido', 'email', 'telefono']
      
      if (mainFields.includes(fieldName)) {
        updateData[fieldName] = newValue
      } else {
        updateData.extra = { [fieldName]: newValue }
      }

      const updatePayload = {
        uniqueFieldValue: String(uniqueFieldValue),
        updateData
      }

      // Actualización optimista
      const updatedUser: DynamicUser = {
        ...localUser,
        // Actualizar campo plano (el servidor aplana los campos extra)
        [fieldName]: newValue,
        ...(updateData.extra && {
          extra: {
            ...(typeof localUser.extra === 'string' ? JSON.parse(localUser.extra) : (localUser.extra ?? {})),
            ...updateData.extra
          }
        })
      }
      
      setLocalUser(updatedUser)
      await updateDynamicUserInDB(updatedUser)

      // Actualizar originalValues
      setOriginalValues(prev => ({ ...prev, [fieldName]: newValue }))
      setHasUnsyncedChanges(true)

      // Sincronización en segundo plano
      if (isOnline) {
        try {
          await updateDynamicUserByUniqueField(updatePayload)
          showToast("success", `Campo ${fieldName} guardado exitosamente`)
        } catch (networkError) {
          await addToQueue({
            type: "update_dynamic_user",
            data: updatePayload,
            timestamp: Date.now(),
          })
          showToast("info", `Campo ${fieldName} guardado localmente`)
        }
      } else {
        await addToQueue({
          type: "update_dynamic_user",
          data: updatePayload,
          timestamp: Date.now(),
        })
        showToast("info", `Campo ${fieldName} guardado localmente`)
      }
      
    } catch (error) {
      console.error(`Error guardando campo ${fieldName}:`, error)
      showToast("error", `Error al guardar el campo ${fieldName}`)
    } finally {
      setFieldSaving(prev => ({ ...prev, [fieldName]: false }))
    }
  }



  // Botón 1: Marcar impreso → PATCH http://localhost:8082/api/users/{folio} (resiliente)
  const handleMarkPrinted = useCallback(async () => {
    if (isGenericLabel) {
      showToast("info", "Las etiquetas genéricas no se marcan como impresas")
      return
    }

    cancelCurrentFetch()

    let localSuccess = false

    try {
      const uniqueField = SchemaService.getUniqueField(schema)
      if (!uniqueField) throw new Error("No se encontró campo único")

      const uniqueFieldValue = SchemaService.getFieldValue(user, uniqueField)
      if (!uniqueFieldValue) throw new Error("Valor del campo único no encontrado")

      // Actualizar IndexedDB local primero
      const updatedUser = { ...localUser, impreso: 1 } as DynamicUser
      await updateDynamicUserInDB(updatedUser)
      setLocalUser(updatedUser)
      localSuccess = true
      console.log('[dynamic-print] ✓ Marcado en IndexedDB local')

      const fields_printed = Object.fromEntries(
        Object.entries(schema)
          .filter(([_, f]) => f.print === true)
          .map(([name]) => [name, true])
      )

      const updatePayload = {
        uniqueFieldValue: String(uniqueFieldValue),
        updateData: {
          impreso: 1,
          label_size: "",
          fields_printed,
          printed_by: printedBy,
        },
      }

      // Intentar actualizar en servidor
      if (isOnline) {
        try {
          await updateDynamicUserByUniqueField(updatePayload)
          console.log('[dynamic-print] ✓ Marcado en servidor')
          showToast("success", "Gafete marcado como impreso")
        } catch (networkError) {
          console.error('[dynamic-print] ✗ Error en servidor, encolando:', networkError)
          await addToQueue({
            type: "update_dynamic_user",
            data: updatePayload,
            timestamp: Date.now(),
          })
          showToast("info", "Marcado localmente. Se sincronizará cuando haya conexión")
        }
      } else {
        await addToQueue({
          type: "update_dynamic_user",
          data: updatePayload,
          timestamp: Date.now(),
        })
        showToast("info", "Marcado localmente. Se sincronizará cuando haya conexión")
      }

      setHasUnsyncedChanges(true)

    } catch (error) {
      console.error('[dynamic-print] ✗ Error al marcar como impreso:', error)
      if (localSuccess) {
        showToast("info", "Marcado localmente pero falló la sincronización")
      } else {
        showToast("error", "Error al marcar como impreso")
      }
    }
  }, [user, localUser, schema, isOnline, isGenericLabel, showToast, cancelCurrentFetch])

  // Botón 2: Imprimir → POST http://127.0.0.1:8080/imprimir + marcar impreso en BD
  // Para etiquetas genéricas → iframe con window.print()
  const handlePrint = useCallback(() => {
    if (isGenericLabel) {
      // Crear iframe oculto para impresión directa (etiquetas genéricas)
      const printFrame = document.createElement('iframe')
      printFrame.style.position = 'absolute'
      printFrame.style.top = '-1000px'
      printFrame.style.left = '-1000px'
      printFrame.style.width = '0'
      printFrame.style.height = '0'
      printFrame.style.border = 'none'

      document.body.appendChild(printFrame)

      const printDocument = printFrame.contentDocument || printFrame.contentWindow?.document
      if (printDocument) {
        // Buscar la imagen de preview generada por LabelPrinter
        const previewImg = document.querySelector('.label-printer-print-only img') as HTMLImageElement
        const previewSrc = previewImg?.src || ''
        
        const labelWidth = 80 // mm
        const labelHeight = 29 // mm

        printDocument.open()
        printDocument.write(`
          <html>
            <head>
              <title>Imprimir Etiqueta</title>
              <style>
                @media print {
                  @page {
                    size: ${labelWidth}mm ${labelHeight}mm;
                    margin: 0;
                  }

                  html, body {
                    width: ${labelWidth}mm !important;
                    height: ${labelHeight}mm !important;
                    margin: 0 !important;
                    padding: 0 !important;
                    overflow: hidden !important;
                  }

                  .label-container {
                    width: ${labelWidth}mm !important;
                    height: ${labelHeight}mm !important;
                    display: flex !important;
                    align-items: flex-start !important;
                    justify-content: flex-start !important;
                  }

                  .label-container img {
                    width: ${labelWidth}mm !important;
                    height: ${labelHeight}mm !important;
                    object-fit: fill !important;
                  }
                }

                body {
                  margin: 0;
                  padding: 0;
                }

                .label-container {
                  display: flex;
                  justify-content: center;
                  align-items: center;
                }

                .label-container img {
                  max-width: 100%;
                  height: auto;
                  display: block;
                }
              </style>
            </head>
            <body>
              <div class="label-container">
                <img src="${previewSrc}" alt="Etiqueta" onload="window.print()" />
              </div>
            </body>
          </html>
        `)
        printDocument.close()

        // Cleanup después de la impresión
        setTimeout(() => {
          document.body.removeChild(printFrame)
        }, 1000)
      }
      return
    }

    const nombre = [
      SchemaService.getFieldValue(user, 'nombre'),
      SchemaService.getFieldValue(user, 'apellido'),
    ].filter(Boolean).join(' ')
    const fixedFieldName = SchemaService.getFixedField(schema)
    const folio = String(
      (fixedFieldName ? SchemaService.getFieldValue(user, fixedFieldName) : null) ?? user.id
    )

    console.log('[dynamic-print] disparando sendToPrinter', { nombre, folio })
    sendToPrinter({
      nombre,
      puesto: String(SchemaService.getFieldValue(user, 'cargo') ?? SchemaService.getFieldValue(user, 'puesto') ?? ''),
      empresa: String(SchemaService.getFieldValue(user, 'empresa_organizacion') ?? SchemaService.getFieldValue(user, 'empresa') ?? ''),
      folio,
    })
      .then(result => {
        if (result.ok) {
          showToast("success", "Gafete enviado a la impresora")
        } else {
          showToast("error", `Impresora: ${result.message}`)
        }
      })
      .catch(() => {
        showToast("error", "No se pudo conectar con la impresora")
      })

    handleMarkPrinted()
  }, [user, schema, isGenericLabel, showToast, handleMarkPrinted])

  // Hook que LabelPrinter espera ANTES de abrir el diálogo de impresión.
  // Devuelve una Promise que resolveremos con el botón del modal de confirmación.
  const handleBeforePrint = useCallback((): boolean | Promise<boolean> => {
    if (isGenericLabel || Number(localUser.impreso) !== 1) {
      return true
    }
    return new Promise<boolean>((resolve) => {
      reprintResolverRef.current = resolve
      setShowReprintConfirm(true)
    })
  }, [isGenericLabel, localUser.impreso])

  const handleConfirmReprint = useCallback(() => {
    setShowReprintConfirm(false)
    reprintResolverRef.current?.(true)
    reprintResolverRef.current = null
  }, [])

  const handleCancelReprint = useCallback(() => {
    setShowReprintConfirm(false)
    reprintResolverRef.current?.(false)
    reprintResolverRef.current = null
  }, [])

  // Cerrar modal
  const handleCloseModal = useCallback(() => {
    if (hasUnsyncedChanges && !isGenericLabel) {
      onPrintSuccess(localUser)
    }
    onClose()
  }, [hasUnsyncedChanges, isGenericLabel, localUser, onPrintSuccess, onClose])



  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleCloseModal} />

        <div className="inline-block align-bottom bg-white/80 backdrop-blur-md border border-gray-200/50 rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full">
          <div className="bg-white/40 backdrop-blur-sm px-6 pt-6 pb-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Configurar Impresión de Gafete
              </h3>
              <button onClick={handleCloseModal} className="text-gray-400 hover:text-gray-600 transition-colors">
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Información del usuario */}
            <div className="mb-6 bg-gray-50 p-3 rounded-lg text-sm text-gray-600">
              <p><strong>Usuario ID:</strong> {localUser.id}</p>
              <p><strong>Estado:</strong> {localUser.impreso ? 'Ya impreso' : 'Sin imprimir'}</p>
              {!isGenericLabel && (
                <p><strong>Tipo:</strong> Usuario real</p>
              )}
              {isGenericLabel && (
                <p><strong>Tipo:</strong> Etiqueta genérica (reutilizable)</p>
              )}
              {missingPrintFields.length > 0 && (
                <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-amber-800">
                  <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500" aria-hidden="true" />
                  <div>
                    <p className="font-semibold">Faltan campos por llenar</p>
                    <p className="mt-0.5">
                      Por favor completa los siguientes campos en el módulo <strong>Editar</strong>:{" "}
                      {missingPrintFields.join(', ')}.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Componente LabelPrinter integrado */}
            <LabelPrinter
              fields={convertToLabelFields()}
              fixedField={getFixedField()}
              category={getCategoryValue()}
              footer={badgeColorFooter}
              size={{ width: 80, height: 28.9, dpi: 300, margin: 2, contentScale: 0.82 }}
              onPrint={handlePrint}
              onMarkPrinted={!isGenericLabel ? handleMarkPrinted : undefined}
              onBeforePrint={handleBeforePrint}
              showControls={true}
              allowEdit={true}
              debug={false}
              className="mb-6"
              isGenericLabel={isGenericLabel}
              onFieldSave={!isGenericLabel ? saveFieldChange : undefined}
              fieldSaving={fieldSaving}
              hasFieldChanged={hasFieldChanged}
              onFieldChange={(fieldName, value) => {
                // Actualizar localUser inmediatamente para reflejar cambios en tiempo real
                const updatedUser = { ...localUser }
                const mainFields = ['nombre', 'apellido', 'email', 'telefono']
                
                if (mainFields.includes(fieldName)) {
                  updatedUser[fieldName as keyof typeof updatedUser] = value
                } else {
                  updatedUser.extra = { ...updatedUser.extra, [fieldName]: value }
                }
                
                setLocalUser(updatedUser)
              }}
              onFixedFieldChange={(value) => {
                // Actualizar campo fijo/QR en tiempo real
                const updatedUser = { ...localUser }
                const fixedFieldName = SchemaService.getFixedField(schema)
                
                if (fixedFieldName) {
                  const mainFields = ['nombre', 'apellido', 'email', 'telefono']
                  
                  if (mainFields.includes(fixedFieldName)) {
                    updatedUser[fixedFieldName as keyof typeof updatedUser] = value
                  } else {
                    updatedUser.extra = { ...updatedUser.extra, [fixedFieldName]: value }
                  }
                }
                
                setLocalUser(updatedUser)
              }}
            />



            {/* Botones de acción */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={handleCloseModal}
                disabled={loading}
                className="px-4 py-2 bg-gray-500/20 backdrop-blur-sm border border-gray-300/30 text-gray-800 text-sm font-medium rounded-xl hover:bg-gray-500/30 hover:border-gray-400/40 hover:shadow-lg hover:shadow-gray-500/20 transition-all duration-300 disabled:opacity-50"
              >
                {isGenericLabel ? 'Cerrar' : 'Cancelar'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de confirmación de reimpresión */}
      {showReprintConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center px-4">
          <div
            className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity"
            onClick={handleCancelReprint}
          />
          <div className="relative bg-white/90 backdrop-blur-md border border-gray-200/60 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-amber-100 border border-amber-200 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-amber-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-semibold text-gray-900">
                    Gafete ya impreso
                  </h3>
                  <p className="mt-1 text-sm text-gray-600">
                    Este gafete ya fue impreso previamente para{" "}
                    <span className="font-medium text-gray-900">
                      {[localUser.nombre, localUser.apellido]
                        .filter(Boolean)
                        .join(" ")
                        .trim() || `ID ${localUser.id}`}
                    </span>
                    . ¿Deseas volver a imprimirlo?
                  </p>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 bg-gray-50/70 border-t border-gray-200/60">
              <button
                type="button"
                onClick={handleCancelReprint}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-100 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleConfirmReprint}
                className="px-4 py-2 text-sm font-medium text-white bg-amber-600 border border-amber-600 rounded-xl hover:bg-amber-700 hover:border-amber-700 shadow-sm transition-colors"
              >
                Volver a imprimir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default DynamicPrintModal
